import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";
import { queryKeys } from "@/lib/queryKeys";
import { normalizeUserRole } from "@/data/RoleContext";

interface UserProfile {
  id: string;
  email?: string;
  name?: string;
  full_name?: string;
  branch_id?: string;
  phone?: string;
  avatar_url?: string;
  is_active?: boolean;
  role?: string;
}

interface UseCurrentUserReturn {
  user: ReturnType<typeof useAuth>["user"];
  profile: UserProfile | null;
  role: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useCurrentUser(): UseCurrentUserReturn {
  const { user: authUser, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const channelName = useRef(`profiles_and_roles_${Math.random().toString(36).slice(2)}`);

  const fetchRole = useCallback(async (profileId: string, forceRefresh = false): Promise<string | null> => {
    const key = queryKeys.userRole(profileId);
    if (forceRefresh) {
      await queryClient.invalidateQueries({ queryKey: key, exact: true });
    }
    return queryClient.fetchQuery({
      queryKey: key,
      queryFn: async () => {
        const { data: userRole, error: userRoleError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", profileId)
          .maybeSingle();
        if (userRoleError) {
          console.error("Error fetching role from user_roles:", userRoleError);
          return null;
        }
        return normalizeUserRole((userRole as { role?: string } | null)?.role ?? null);
      },
      staleTime: 60_000,
      gcTime: 10 * 60_000,
      retry: 1,
    });
  }, [queryClient]);

  const loadProfileRow = useCallback(async (userId: string): Promise<UserProfile | null> => {
    const profileSelect = "id, auth_user_id, email, full_name, first_name, last_name, is_active, role";

    const { data: profileByAuthUserId, error: profileByAuthUserIdError } = await supabase
      .from("profiles")
      .select(profileSelect)
      .eq("auth_user_id", userId)
      .maybeSingle();

    if (profileByAuthUserIdError) {
      console.error("Error fetching profile by auth_user_id:", profileByAuthUserIdError);
      return null;
    }

    if (profileByAuthUserId) {
      return profileByAuthUserId as UserProfile;
    }

    const { data: profileById, error: profileByIdError } = await supabase
      .from("profiles")
      .select(profileSelect)
      .eq("id", userId)
      .maybeSingle();

    if (profileByIdError) {
      console.error("Error fetching profile by id:", profileByIdError);
      return null;
    }

    return (profileById as UserProfile | null) ?? null;
  }, []);

  const fetchProfile = useCallback(async (userId: string, options?: { forceProfileRefresh?: boolean; forceRoleRefresh?: boolean }) => {
    try {
      const profileKey = queryKeys.currentUserProfile(userId);
      if (options?.forceProfileRefresh) {
        await queryClient.invalidateQueries({ queryKey: profileKey, exact: true });
      }
      const data = await queryClient.fetchQuery({
        queryKey: profileKey,
        queryFn: async () => loadProfileRow(userId),
        staleTime: 60_000,
        gcTime: 10 * 60_000,
        retry: 1,
      });

      if (data) {
        const typedData = data as UserProfile;
        const nextProfileId = typedData.id ?? null;
        setProfileId(nextProfileId);
        const resolvedRole = await fetchRole(nextProfileId, options?.forceRoleRefresh === true);

        // user_roles is source of truth. Keep profiles.role as transitional fallback only.
        setProfile({
          ...typedData,
          role: resolvedRole ?? normalizeUserRole(typedData.role ?? null) ?? undefined,
        });
      } else {
        setProfile(null);
        setProfileId(null);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  }, [fetchRole, loadProfileRow]);

  const refresh = useCallback(async () => {
    if (authUser) {
      await fetchProfile(authUser.id, { forceProfileRefresh: true, forceRoleRefresh: true });
    }
  }, [authUser, fetchProfile]);

  useEffect(() => {
    const init = async () => {
      if (authLoading) {
        return;
      }

      if (authUser) {
        await fetchProfile(authUser.id);
      } else {
        setProfile(null);
        setProfileId(null);
      }

      setLoading(false);
    };

    init();
  }, [authUser, authLoading, fetchProfile]);

  useEffect(() => {
    if (!authUser) return;

    const profileChannel = supabase
      .channel(channelName.current)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
          filter: `auth_user_id=eq.${authUser.id}`,
        },
        (payload) => {
          if (payload.new) {
            void fetchProfile(authUser.id, { forceProfileRefresh: true });
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${authUser.id}`,
        },
        (payload) => {
          if (payload.new) {
            void fetchProfile(authUser.id, { forceProfileRefresh: true });
          }
        }
      )
      .subscribe();

    let roleChannel: ReturnType<typeof supabase.channel> | null = null;
    if (profileId) {
      roleChannel = supabase
        .channel(`${channelName.current}_roles`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "user_roles",
            filter: `user_id=eq.${profileId}`,
          },
          () => {
            void queryClient.invalidateQueries({
              queryKey: queryKeys.userRole(profileId),
              exact: true,
            });
            void fetchProfile(authUser.id, { forceProfileRefresh: true, forceRoleRefresh: true });
          }
        )
        .subscribe();
    }

    return () => {
      supabase.removeChannel(profileChannel);
      if (roleChannel) supabase.removeChannel(roleChannel);
    };
  }, [authUser, fetchProfile, profileId, queryClient]);

  return {
    user: authUser,
    profile,
    role: profile?.role ?? null,
    loading: loading || authLoading,
    refresh,
  };
}
